import React, { useEffect, useState } from "react";
import ChartLineCompetitionProjection from "shared/components/charts/ChartLineCompetitionProjection";
import ChartLineCompetitionProbability from "shared/components/charts/ChartLineCompetitionProbability";
import ChartLineCompetitionRanking from "shared/components/charts/ChartLineCompetitionRanking";
import ItemRowCompetition from "shared/components/items/ItemRowCompetition";
import PopupClub from "shared/components/popups/PopupClub";
import ButtonCheckbox from "../buttons/ButtonCheckbox";
import ButtonSelect from "../buttons/ButtonSelect.js";
import { isUserAdmin } from "shared/utils/user.js";
import LoadingSquare from "shared/components/loading/LoadingSquare";

/*interface MiscCompetitionRankingProps {
  competition: any;
  clubToHighlight?: number[];
  initialView?: "ranking" | "projection";
  clubsToShow?: any;
  hideViewButton?: any;
}*/

const MiscCompetitionRanking = ({
  competition,
  clubToHighlight,
  initialView,
  clubsToShow,
  hideViewButton,
  hideProbability = false,
}) => {
  const [view, setView] = useState(initialView ? initialView : "ranking");
  const [selectedMetric, setSelectedMetric] = useState("mmr");
  const [isHistoryActive, setIsHistoryActive] = useState(false);

  const latestRanking =
    competition.rankingHistory && competition.rankingHistory.length > 0
      ? competition.rankingHistory[competition.rankingHistory.length - 1].rankings
      : [];

  const latestRankingDate =
    competition.rankingHistory && competition.rankingHistory.length > 0
      ? competition.rankingHistory[competition.rankingHistory.length - 1].date
      : null;

  const getClubInfo = (clubId) => competition.participants.find((p) => p.id === clubId);

  const sortedClubs = competition.participants
    ? [...competition.participants]
        .map((p) => {
          if (competition.status === "LIVE" || competition.status === "PLANNED") {
            return p;
          }

          const lastHistory = [...p.history]
            .filter((h) => latestRankingDate && h.date <= latestRankingDate)
            .reverse()
            .find((h) => h[selectedMetric] != null);
          return { ...p, lastHistory };
        })
        .sort((a, b) => (b[selectedMetric] ?? 0) - (a[selectedMetric] ?? 0))
    : [];

  const mmrSortedClubs = competition.participants
    ? [...competition.participants]
        .map((p) => {
          if (competition.status === "LIVE" || competition.status === "PLANNED") {
            return p;
          }

          const lastHistory = [...p.history]
            .filter((h) => latestRankingDate && h.date <= latestRankingDate)
            .reverse()
            .find((h) => h.mmr != null);
          return { id: p.id, mmr: lastHistory?.mmr ?? 0 };
        })
        .sort((a, b) => b.mmr - a.mmr)
        .map((c, i) => ({ clubId: c.id, projectedPosition: i + 1 }))
    : [];

  const getProjectedPosition = (clubId) => mmrSortedClubs.find((c) => c.clubId === clubId)?.projectedPosition ?? 0;

  const allowedClubIds =
    competition.type === "CUP" && clubToHighlight?.length === 1
      ? competition.groups?.filter((g) => g.clubIds?.includes(clubToHighlight[0])).flatMap((g) => g.clubIds) || []
      : undefined;

  useEffect(() => {
    if (initialView === "ranking" || initialView === "projection" || initialView === "probability") {
      setView(initialView);
      setSelectedMetric("mmr");
    }
  }, [initialView]);

  return (
    <div key={competition.id} className="background-color pt-1 p-2 mb-2 w-100">
      <div className="d-flex flex-grow-1 flex-column mb-2">
        <div className="d-flex flex-grow-1 h5 mb-0">
          <ItemRowCompetition c={competition} hideSeason={true} className={"Item-dark"} />
        </div>
        <div className="d-flex flex-grow-0 justify-content-end flex-column flex-sm-row flex-column-reverse align-content-end align-items-sm-center">
          {!hideViewButton && (
            <div className="d-flex justify-content-end">
              <ButtonCheckbox
                label={"History"}
                checked={isHistoryActive}
                checkboxEnd={true}
                onChange={(v) => {
                  setIsHistoryActive(!isHistoryActive);
                }}
              />
            </div>
          )}
          {!hideViewButton && (
            <div className="d-flex flex-row ms-md-2 me-1 justify-content-end align-items-center">
              <ButtonSelect
                className="d-flex border"
                buttons={{ ranking: "Ranking", projection: "Projection", probability: "Probability" }}
                selected={view}
                onChange={setView}
              />
            </div>
          )}
        </div>
      </div>

      {!isHistoryActive ? (
        <>
          <div className="d-flex flex-row align-items-right px-1" style={{ maxWidth: "100%" }}>
            {view === "ranking" && (
              <>
                <div className="d-flex justify-content-center" style={{ minWidth: 30 }}>
                  #
                </div>
                <div className="flex-grow-1 text-truncate">Club</div>
                <div className="d-flex justify-content-center" style={{ minWidth: 30 }}>
                  Gm
                </div>
                <div className="d-flex justify-content-center d-none d-sm-flex" style={{ minWidth: 30 }}>
                  W
                </div>
                <div className="d-flex justify-content-center d-none d-sm-flex" style={{ minWidth: 30 }}>
                  D
                </div>
                <div className="d-flex justify-content-center d-none d-sm-flex" style={{ minWidth: 30 }}>
                  L
                </div>
                <div className="d-flex justify-content-center d-none d-sm-flex" style={{ minWidth: 30 }}>
                  GD
                </div>
                <div className="d-flex justify-content-center" style={{ minWidth: 30 }}>
                  Pts
                </div>
                <div className="d-flex justify-content-center" style={{ minWidth: 30 }}>
                  Δ
                </div>
              </>
            )}
            {view === "projection" && (
              <>
                <div className="d-flex justify-content-center" style={{ minWidth: 30 }}>
                  #
                </div>
                <div className="flex-grow-1 text-truncate">Club</div>

                <div
                  className={`d-flex justify-content-center d-none d-sm-flex ${
                    selectedMetric === "playerCount" ? "text-main fw-bold" : ""
                  }`}
                  style={{ minWidth: 30, cursor: "pointer" }}
                  onClick={() => setSelectedMetric("playerCount")}
                >
                  Nb
                </div>
                <div
                  className={`d-flex justify-content-center ${selectedMetric === "b11" ? "text-main fw-bold" : ""}`}
                  style={{ minWidth: 50, cursor: "pointer" }}
                  onClick={() => setSelectedMetric("b11")}
                >
                  B11
                </div>
                <div
                  className={`d-flex justify-content-center d-none d-sm-flex ${
                    selectedMetric === "b11a" ? "text-main fw-bold" : ""
                  }`}
                  style={{ minWidth: 50, cursor: "pointer" }}
                  onClick={() => setSelectedMetric("b11a")}
                >
                  B11a
                </div>
                <div
                  className={`d-flex justify-content-center d-none d-sm-flex ${
                    selectedMetric === "b16a" ? "text-main fw-bold" : ""
                  }`}
                  style={{ minWidth: 50, cursor: "pointer" }}
                  onClick={() => setSelectedMetric("b16a")}
                >
                  B16a
                </div>
                <div
                  className={`d-flex justify-content-center d-none d-sm-flex ${
                    selectedMetric === "ig11" ? "text-main fw-bold" : ""
                  }`}
                  style={{ minWidth: 50, cursor: "pointer" }}
                  onClick={() => setSelectedMetric("ig11")}
                >
                  IG11
                </div>
                <div
                  className={`d-flex justify-content-center ${selectedMetric === "mmr" ? "text-main fw-bold" : ""}`}
                  style={{ minWidth: 50, cursor: "pointer" }}
                  onClick={() => setSelectedMetric("mmr")}
                >
                  MMR
                </div>
              </>
            )}
            {view === "probability" && (
              <>
                <div className="d-flex justify-content-center" style={{ minWidth: 30 }}>
                  #
                </div>
                <div className="flex-grow-1 text-truncate">Club</div>
                <div className="d-flex justify-content-center" style={{ minWidth: 50 }}>
                  ⬇️%
                </div>
                <div className="d-flex justify-content-center" style={{ minWidth: 50 }}>
                  ⬆️%
                </div>
                <div className="d-flex justify-content-center" style={{ minWidth: 50 }}>
                  🏆%
                </div>
                <div className="d-flex justify-content-center" style={{ minWidth: 30 }}>
                  Gm
                </div>
                <div className="d-flex justify-content-center" style={{ minWidth: 30 }}>
                  Pts
                </div>
                <div className="d-flex justify-content-center" style={{ minWidth: 30 }}>
                  Δ
                </div>
              </>
            )}
          </div>

          {(view === "ranking" || view === "probability") &&
            (latestRanking ? (
              latestRanking.map((c, index) => {
                const club = getClubInfo(c.clubId);
                const isMyClub = clubToHighlight?.includes(c.clubId);

                if (!club || (clubsToShow && !clubsToShow.includes(c.clubId))) return null;

                if (
                  competition.type === "CUP" &&
                  clubToHighlight?.length === 1 &&
                  competition?.groups?.filter((g) => g.clubIds?.includes(clubToHighlight[0])).length > 0 &&
                  !competition?.groups
                    ?.filter((g) => g.clubIds?.includes(clubToHighlight[0]))[0]
                    .clubIds.includes(club.id)
                ) {
                  return null;
                }

                const projectedPosition = getProjectedPosition(c.clubId);
                const delta = projectedPosition - (index + 1);

                return (
                  <PopupClub
                    key={c.clubId}
                    className="flex-fill"
                    club={club}
                    trigger={
                      <div
                        className={
                          "Item d-flex flex-fill justify-content-between align-items-right border-top " +
                          (isMyClub ? "selected" : "")
                        }
                        style={!isMyClub ? { backgroundColor: "transparent" } : {}}
                      >
                        <div className="d-flex justify-content-center" style={{ minWidth: 30 }}>
                          {index + 1}
                        </div>
                        <div
                          className="d-flex flex-fill text-truncate position-relative"
                          style={{ minWidth: 0, flexShrink: 1 }}
                        >
                          <div className="d-flex justify-content-center" style={{ minWidth: 25, flexShrink: 0 }}>
                            <img
                              className="mt-1"
                              src={`https://d13e14gtps4iwl.cloudfront.net/u/clubs/${club.id}/logo.webp`}
                              alt={`${club.name} logo`}
                              style={{ width: "15px", height: "15px" }}
                            />
                          </div>
                          <div
                            className="text-truncate"
                            style={{
                              position: "absolute",
                              left: 30,
                              right: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {club.name}
                          </div>
                        </div>
                        {view === "probability" && (
                          <>
                            <div className="d-flex justify-content-center" style={{ minWidth: 50 }}>
                              {!c.relegationProbability || c.relegationProbability.toFixed(1) === 0 || hideProbability
                                ? "--"
                                : c.relegationProbability.toFixed(1)}
                            </div>
                            <div className="d-flex justify-content-center" style={{ minWidth: 50 }}>
                              {!c.promotionProbability || c.promotionProbability.toFixed(1) === 0 || hideProbability
                                ? "--"
                                : c.promotionProbability.toFixed(1)}
                            </div>
                            <div className="d-flex justify-content-center" style={{ minWidth: 50 }}>
                              {!c.trophyProbability || c.trophyProbability.toFixed(1) === 0 || hideProbability
                                ? "--"
                                : c.trophyProbability.toFixed(1)}
                            </div>
                          </>
                        )}
                        <div className="d-flex justify-content-center" style={{ minWidth: 30 }}>
                          {c.played}
                        </div>
                        {view === "ranking" && (
                          <>
                            <div className="d-flex justify-content-center d-none d-sm-flex" style={{ minWidth: 30 }}>
                              {c.wins}
                            </div>
                            <div className="d-flex justify-content-center d-none d-sm-flex" style={{ minWidth: 30 }}>
                              {c.draws}
                            </div>
                            <div className="d-flex justify-content-center d-none d-sm-flex" style={{ minWidth: 30 }}>
                              {c.losses}
                            </div>
                            <div className="d-flex justify-content-center d-none d-sm-flex" style={{ minWidth: 30 }}>
                              {(c.goals || 0) - (c.goalsAgainst || 0)}
                            </div>
                          </>
                        )}

                        <div className="d-flex justify-content-center" style={{ minWidth: 30 }}>
                          {c.points}
                        </div>
                        <div className="d-flex justify-content-center" style={{ minWidth: 30 }}>
                          {competition.status === "LIVE" ? (
                            <span className={delta > 0 ? "main-blue-color" : delta < 0 ? "main-pink-color" : ""}>
                              {delta > 0 ? `+${delta}` : delta}
                            </span>
                          ) : (
                            "--"
                          )}
                        </div>
                      </div>
                    }
                  />
                );
              })
            ) : (
              <LoadingSquare height={280} />
            ))}

          {view === "projection" &&
            sortedClubs.map((c, index) => {
              let clubsToHighlight = clubToHighlight;
              let club;

              if (competition.status === "LIVE" || competition.status === "PLANNED") {
                club = c;
              } else {
                const lastHistory = [...c.history]
                  .filter((h) => latestRankingDate && h.date <= latestRankingDate)
                  .reverse()
                  .find((h) => h[selectedMetric] != null);
                club = { ...c, lastHistory };
              }

              if (clubsToShow && !clubsToShow.includes(club.id)) return;

              if (competition.type === "CUP" && clubToHighlight?.length === 1) {
                const group = competition?.groups?.filter((g) => g.clubIds?.includes(clubToHighlight[0])).pop();
                if (group) clubsToHighlight = group.clubIds;
              }

              const values = competition.participants
                ? competition.participants.map((p) => {
                    const lastHistory = [...p.history].reverse().find((h) => h[selectedMetric] != null);
                    return lastHistory?.[selectedMetric] ?? 0;
                  })
                : [0];
              const minVal = Math.min(...values);
              const maxVal = Math.max(...values);

              const getCellStyle = (metric) => {
                if (metric !== selectedMetric) return { minWidth: 50 };
                const val = club[metric];
                if (val == null) return { minWidth: 50 };
                const definedValues = competition.participants
                  ? competition.participants
                      .map((p) => {
                        const lastHistory = [...p.history].reverse().find((h) => h[selectedMetric] != null);
                        return lastHistory?.[selectedMetric];
                      })
                      .filter((v) => v != null) // keep only defined values
                  : [0];
                const minVal = Math.min(...definedValues);
                const maxVal = Math.max(...definedValues);
                const opacity = maxVal !== minVal ? 0.1 + 0.6 * ((val - minVal) / (maxVal - minVal)) : 0.7;
                return {
                  minWidth: 50,
                  backgroundColor: `rgba(var(--primary-color-rgb), ${opacity})`,
                  textAlign: "center",
                };
              };

              return (
                <PopupClub
                  key={club.id}
                  className="flex-fill"
                  club={club}
                  trigger={
                    <div
                      className={
                        "Item d-flex flex-fill justify-content-between align-items-right border-top " +
                        (clubsToHighlight?.includes(club.id) ? "selected" : "")
                      }
                      style={!clubsToHighlight?.includes(club.id) ? { backgroundColor: "transparent" } : {}}
                    >
                      <div className="d-flex justify-content-center" style={{ minWidth: 30 }}>
                        {index + 1}
                      </div>
                      <div
                        className="d-flex flex-fill text-truncate position-relative"
                        style={{ minWidth: 0, flexShrink: 1 }}
                      >
                        <div className="d-flex justify-content-center" style={{ minWidth: 25, flexShrink: 0 }}>
                          <img
                            className="mt-1"
                            src={`https://d13e14gtps4iwl.cloudfront.net/u/clubs/${club.id}/logo.webp`}
                            alt={`${club.name} logo`}
                            style={{ width: "15px", height: "15px" }}
                          />
                        </div>
                        <div
                          className="text-truncate"
                          style={{
                            position: "absolute",
                            left: 30,
                            right: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {club.name}
                        </div>
                      </div>
                      <div
                        className="d-flex justify-content-center d-none d-sm-flex"
                        style={{ ...getCellStyle("playerCount"), minWidth: "30px" }}
                      >
                        {club.playerCount != null ? club.playerCount.toFixed(0) : "-"}
                      </div>
                      <div className="d-flex justify-content-center" style={getCellStyle("b11")}>
                        {club.b11 != null ? club.b11.toFixed(0) : "-"}
                      </div>
                      <div className="d-flex justify-content-center d-none d-sm-flex" style={getCellStyle("b11a")}>
                        {club.b11a != null ? club.b11a.toFixed(1) : "-"}
                      </div>
                      <div className="d-flex justify-content-center d-none d-sm-flex" style={getCellStyle("b16a")}>
                        {club.b16a != null ? club.b16a.toFixed(1) : "-"}
                      </div>
                      <div className="d-flex justify-content-center d-none d-sm-flex" style={getCellStyle("ig11")}>
                        {club.ig11 != null ? club.ig11.toFixed(1) : "-"}
                      </div>
                      <div className="d-flex justify-content-center" style={getCellStyle("mmr")}>
                        {club.mmr != null ? club.mmr.toFixed(1) : "-"}
                      </div>
                    </div>
                  }
                />
              );
            })}
        </>
      ) : (
        <div className="d-flex flex-fill ratio ratio-16x9">
          {view === "ranking" && (
            <ChartLineCompetitionRanking
              data={
                allowedClubIds
                  ? {
                      ...competition,
                      participants: competition.participants.filter((p) => allowedClubIds.includes(p.id)),
                    }
                  : competition
              }
              clubToHighlight={clubToHighlight?.[0]}
            />
          )}
          {view === "projection" && (
            <ChartLineCompetitionProjection
              data={
                allowedClubIds
                  ? {
                      ...competition,
                      participants: competition.participants.filter((p) => allowedClubIds.includes(p.id)),
                    }
                  : competition
              }
              clubToHighlight={clubToHighlight?.[0]}
            />
          )}
          {view === "probability" && (
            <ChartLineCompetitionProbability
              data={
                allowedClubIds
                  ? {
                      ...competition,
                      participants: competition.participants.filter((p) => allowedClubIds.includes(p.id)),
                    }
                  : competition
              }
              clubToHighlight={clubToHighlight?.[0]}
              hideProbability={hideProbability}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default MiscCompetitionRanking;
